import _ from 'lodash';

import { E消費税区分, E税区分, Eフェーズ種別, E課税方式, E集計転記科目, E有無フラグ } from '@/openapi/enum';
import {
  CデータPlbscfItemCommon,
  Cデータ納税予測,
  データListItemResponse,
  帳票DynamicConfig,
  法人税,
  法人税Label,
  法人税加減算値,
  納税YearValue,
} from '@/openapi/user';

import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';
import 償却Service from '@/classes/service/分析計画/償却Service';
import { 償却 } from '@/classes/service/分析計画/償却Type';
import 決算期Utility, { 決算期, 決算期TableHeader } from '@/classes/common/決算期Utility';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import StorageDao from '@/classes/common/StorageDao';

// ------------------------------------------------------------------
// enums
// ------------------------------------------------------------------

/** 行設定（帳票のテンプレート行と一致する想定） */
export enum ListItemConfig {
  title,
  header,
  subHeader,
  total,
  subTotal,
  input,
  input期首残高,
  inputTotal,
  empty,
}

/** セル種別 */
export enum CellTagType {
  inputCurrency,
  inputPercent,
  inputText,
  inputTotalCurrency,
  currency,
  text,
}

// ------------------------------------------------------------------
// types
// ------------------------------------------------------------------

/** parseの返却値 */
export type 納税予測 = {
  データpk?: number;
  cデータ: Cデータ納税予測;
  header: 決算期TableHeader[];
  tables: 納税予測Table[];
};

/** 納税予測表 */
type 納税予測Table = {
  title: string;
  items: 納税予測ListItem[];
};

/** 納税予測表の行 */
export type 納税予測ListItem = {
  cells: Cell[];
  config: ListItemConfig;
};

/** 納税予測表のセル */
export type Cell = {
  tagType: CellTagType;
  value: {
    fix?: string | number;
    reference?: {
      object: Record<string, unknown>;
      key: string | number;
    };
  };
  unit?: string;
};

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------

const CODE_OF_税引前当期純利益 = 'N223';

// ------------------------------------------------------------------
// 納税予測Service
// ------------------------------------------------------------------

export default class 納税予測Service extends AbstractデータService<納税予測> {
  // ------------------------------------------------------------------
  // fields
  // ------------------------------------------------------------------

  protected serviceType = ServiceType.納税予測;

  private readonly 法人税ListItemCount = 5;

  private 決算期: 決算期;

  private 計画PlbscfService: 計画PlbscfService | null = null;

  private 償却Service: 償却Service | null = null;

  private 償却: 償却 | null = null;

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    this.決算期 = 決算期Utility.get計画決算期();
    this.setBnRoundingMode();
  }

  // ------------------------------------------------------------------
  // overrides
  // ------------------------------------------------------------------

  public parse(データs: データListItemResponse[], option?: Record<string, AbstractデータService<unknown>>): 納税予測 {
    // 他service 初期化
    if (this.計画PlbscfService === null) {
      this.計画PlbscfService =
        option?.計画PlbscfService === undefined
          ? new 計画PlbscfService(this.eフェーズ種別)
          : (option?.計画PlbscfService as 計画PlbscfService);
    }
    this.計画PlbscfService.set勘定科目s = this.勘定科目s;
    this.計画PlbscfService.set部門s = this.部門s;
    this.計画PlbscfService.parse(データs);

    if (this.償却Service === null) {
      this.償却Service =
        option?.償却Service === undefined
          ? new 償却Service(
              this.eフェーズ種別 === Eフェーズ種別.分析フェーズ
                ? Eフェーズ種別.分析フェーズ
                : Eフェーズ種別.計画フェーズ
            )
          : (option?.償却Service as 償却Service);
    }
    this.償却Service.set勘定科目s = this.勘定科目s;
    this.償却 = this.償却Service.parse(データs, { functionType: '設備投資' });

    const 納税予測データ = this.findBy入力分類(データs);
    const cデータ =
      納税予測データ !== undefined
        ? (JSON.parse(納税予測データ.cデータ) as Cデータ納税予測)
        : this.createInitialCデータ納税予測();

    const v納税予測: 納税予測 = {
      データpk: 納税予測データ?.pk,
      cデータ,
      header: this.決算期.header,
      tables: this.createTables(cデータ),
    };

    return v納税予測;
  }

  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 帳票データ {
    const raw = this.parse(データs, option);

    // 初期化
    const templateRows: number[] = [];
    const values: unknown[][] = [];
    const dynamicConfigs: 帳票DynamicConfig[] =
      raw.header.length > 1
        ? [
            {
              columns: [0, 1, ...new Array(raw.header.length - 1).fill(2)],
            },
          ]
        : [];
    const cellCount = raw.header.length + 1;

    // 以下帳票データ整形
    raw.tables.forEach((table) => {
      // 表のタイトル
      templateRows.push(ListItemConfig.title);
      values.push([table.title, ...this.templateCellValues(raw.header.length)]);
      // 決算期ヘッダ
      templateRows.push(ListItemConfig.header);
      values.push([AbstractデータService.getMoneyUnitLabel(), ...raw.header.map((h) => h.label)]);
      // listitems
      table.items.forEach((item) => {
        templateRows.push(item.config);
        const cellValues: unknown[] = [];
        item.cells.forEach((cell) => {
          let value: unknown = null;
          switch (cell.tagType) {
            case CellTagType.inputCurrency:
            case CellTagType.inputPercent:
            case CellTagType.inputText:
              const reference = cell.value.reference!;
              const inputValue = reference.object[reference.key];
              value =
                cell.tagType === CellTagType.inputCurrency ? this.getMoneyUnitValue(Number(inputValue)) : inputValue;
              break;
            case CellTagType.inputTotalCurrency:
              value = this.getMoneyUnitValue(Number(this.getCellValue(cell)));
              break;
            case CellTagType.currency:
              value = this.getMoneyUnitValue(Number(cell.value.fix));
              break;
            case CellTagType.text:
              value = cell.value.fix;
              break;
          }
          if (cell.unit !== undefined) value += cell.unit;
          cellValues.push(value);
        });
        if (cellValues.length < cellCount) cellValues.push(...this.templateCellValues(cellCount - cellValues.length));
        values.push(cellValues);
      });
      // 空行
      templateRows.push(ListItemConfig.empty);
      values.push([]);
    });

    return {
      templateRows,
      values,
      dynamicConfigs,
    };
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 納税予測表を生成します
   *
   * @param cデータ
   * @returns 納税予測Table[]
   */
  public createTables(cデータ: Cデータ納税予測): 納税予測Table[] {
    const tables = [this.create法人税Table(cデータ), this.create繰越欠損金Table(cデータ)];

    // 課税事業者の場合は消費税表も追加
    if (AbstractデータService.事業所.c消費税区分 === E消費税区分.課税事業者) {
      tables.push(this.create消費税Table(cデータ));
    }

    return tables;
  }

  /**
   * 貸借のmonthlyValuesを設定する
   * ※画面側から呼ぶ
   * ※貸借の月次の値は、損益のように年次の値を12等分せず、最終月のみに値を設定する
   *
   * @param item
   * @param 勘定科目Pk
   * @param yearlyValue
   * @param year
   * @returns
   */
  public setMonthlyValuesFor貸借(
    item: CデータPlbscfItemCommon,
    勘定科目Pk: number,
    yearlyValue: number,
    year: number
  ): void {
    // plbscf のデータを勘定科目Pkで検索
    const tデータPlbscfs = this.計画PlbscfService!.getTデータPlbscfs;
    const cデータPlbscfItem = tデータPlbscfs
      .find((tデータPlbscf) => tデータPlbscf.番号 === year && tデータPlbscf.部門pk === undefined)
      ?.items.find((item) => item.勘定科目Pk === 勘定科目Pk);

    if (cデータPlbscfItem === undefined) {
      // plbscf のデータが存在しない場合
      item.monthlyValues = new Array(MONTHS_OF_YEAR).fill(0);
      item.monthlyValues[MONTHS_OF_YEAR - 1] = yearlyValue;
    } else {
      // 存在する場合
      item.monthlyValues = cデータPlbscfItem.monthlyValues!;
      item.monthlyValues[MONTHS_OF_YEAR - 1] = yearlyValue;
      // 合計値算出
      item.value = _.sum(item.monthlyValues);
    }
  }

  /**
   * セルの値を返却します
   * referenceが存在する場合はreference、存在しない場合はfixを返却します
   *
   * @param cell
   * @returns
   */
  public getCellValue(cell: Cell): unknown {
    const ref = cell.value.reference?.object[cell.value.reference?.key];
    return ref === undefined ? cell.value.fix : ref;
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 指定セル一覧から固定値を数値の一覧で取得します
   *
   * @param cells
   * @returns number[]
   */
  private getCellFixNumbers(...cells: Cell[]): number[] {
    return cells.map((cell) => {
      const fix = cell.value.fix;
      return fix !== undefined ? Number(fix) : 0;
    });
  }

  /**
   * 指定セル一覧から数値の一覧で取得します
   *
   * @param cells
   * @returns number[]
   */
  private getCellNumbers(...cells: Cell[]): number[] {
    return cells.map((cell) => {
      const value = this.getCellValue(cell);
      return value !== undefined ? Number(value) : 0;
    });
  }

  /**
   * オブジェクト配列を生成します
   *
   * @param object
   * @param count
   * @returns T[]
   */
  private createObjectArray<T>(object: T, count: number): T[] {
    return new Array(count).fill(null).map(() => _.cloneDeep(object));
  }

  /**
   * 入力値セルの情報を生成します
   *
   * @param tagType
   * @param object
   * @param key
   * @param unit
   * @returns Cell
   */
  private createInputCell<T extends Record<keyof T, unknown> | Record<keyof T, undefined>>(
    tagType: CellTagType,
    object: T,
    key: string | number,
    unit?: string
  ): Cell {
    const reference = {
      object,
      key,
    };
    return this.createCell(tagType, undefined, reference, unit);
  }

  /**
   * 計算値セルの情報を生成します
   *
   * @param tagType
   * @param fix
   * @param unit
   * @returns Cell
   */
  private createFixCell(tagType: CellTagType, fix: string | number, unit?: string): Cell {
    return this.createCell(tagType, fix, undefined, unit);
  }

  /**
   * 計算値or入力値セルの情報を生成します
   * 入力値のデータがない場合は計算値を設定します
   *
   * @param tagType
   * @param fix
   * @param reference
   * @param unit
   * @returns Cell
   */
  private createCell<T extends Record<keyof T, unknown> | Record<keyof T, undefined>>(
    tagType: CellTagType,
    fix?: string | number,
    reference?: {
      object: T;
      key: string | number;
    },
    unit?: string
  ): Cell {
    return {
      tagType,
      value: {
        fix,
        reference,
      },
      unit,
    };
  }

  /**
   * 納税予測ListItemを生成します
   *
   * @param label
   * @param cells
   * @param config
   * @returns 納税予測ListItem
   */
  private create納税予測ListItem(
    label: string | E集計転記科目.法人税等 | E集計転記科目.未払法人税等 | E集計転記科目.未払消費税等,
    cells: Cell[],
    config: ListItemConfig
  ): 納税予測ListItem {
    return {
      cells: [this.createFixCell(CellTagType.text, label), ...cells],
      config,
    };
  }

  /**
   * Cデータ納税予測を生成します
   *
   * @returns Cデータ納税予測
   */
  private createInitialCデータ納税予測(): Cデータ納税予測 {
    // 法人税Label生成
    const v法人税Label: 法人税Label = {
      加算: '',
      減算: '',
    };
    const 法人税Labels = this.createObjectArray(v法人税Label, this.法人税ListItemCount);

    // yearValues生成
    const v法人税加減算値: 法人税加減算値 = {
      加算: 0,
      減算: 0,
    };
    const v法人税: 法人税 = {
      加減算値s: this.createObjectArray(v法人税加減算値, this.法人税ListItemCount),
      実効税率: 0,
      予定納税額: 0,
    };
    const yearValue0期: 納税YearValue = {
      法人税: v法人税,
      繰越欠損金: {
        残高: 0,
        増加額: 0,
        減少額: 0,
      },
      消費税中間納付額: 0,
    };
    const yearValue: 納税YearValue = {
      法人税: v法人税,
      繰越欠損金: {
        増加額: 0,
        減少額: 0,
      },
      消費税中間納付額: 0,
    };
    const yearValues = [
      ...this.createObjectArray(yearValue0期, 1),
      ...this.createObjectArray(yearValue, this.決算期.years.length - 1),
    ];

    return {
      法人税Labels,
      yearValues,
    };
  }

  private roundCellFixNumb(numb: number, round: number): number {
    return numb < 0 ? Math.ceil(numb / round) * round : Math.floor(numb / round) * round;
  }

  private getFirstYear(): number {
    const 計画0期Year = 決算期Utility.getYear(StorageDao.get計画書().c計画0期の決算期);
    return StorageDao.get計画書Local().c第1期月次作成フラグ === E有無フラグ.あり ? 計画0期Year + 1 : 計画0期Year;
  }

  /**
   * cデータ から法人税の 納税予測Table を生成します
   *
   * @param cデータ
   * @returns 納税予測Table
   */
  private create法人税Table(cデータ: Cデータ納税予測): 納税予測Table {
    // 加算、減算一覧生成関数
    const create加減算s = (key: keyof 法人税加減算値): 納税予測ListItem[] => {
      return cデータ.法人税Labels.map(
        (label, i): 納税予測ListItem => ({
          cells: [
            this.createInputCell(CellTagType.inputText, label, key),
            ...cデータ.yearValues.map((v) =>
              this.createInputCell(CellTagType.inputCurrency, v.法人税.加減算値s[i], key)
            ),
          ],
          config: ListItemConfig.input,
        })
      );
    };

    // 税引前当期利益
    const 税引前当期利益CalcCells = this.決算期.years.map((y) => {
      const 税引前当期利益CalcValue = this.計画PlbscfService!.getValueByCodeAnd決算期年月(
        CODE_OF_税引前当期純利益,
        y.year,
        undefined,
        y.year > this.getFirstYear()
      );
      return this.createFixCell(CellTagType.currency, 税引前当期利益CalcValue);
    });
    const 税引前当期利益 = this.create納税予測ListItem(
      '①　税引前当期利益',
      税引前当期利益CalcCells,
      ListItemConfig.subTotal
    );

    // 加算ヘッダー
    const 加算Header = this.create納税予測ListItem('【加算】', [], ListItemConfig.subHeader);

    // 加算一覧
    const 加算s = create加減算s('加算');

    // 加算小計
    const 加算小計CalcCells = cデータ.yearValues.map((v) => {
      return this.createFixCell(CellTagType.currency, _.sumBy(v.法人税.加減算値s, '加算'));
    });
    const 加算小計 = this.create納税予測ListItem('②　小計', 加算小計CalcCells, ListItemConfig.subTotal);

    // 減算ヘッダー
    const 減算Header = this.create納税予測ListItem('【減算】', [], ListItemConfig.subHeader);

    // 減算一覧
    const 減算s = create加減算s('減算');

    // 減算小計
    const 減算小計CalcCells = cデータ.yearValues.map((v) => {
      return this.createFixCell(CellTagType.currency, _.sumBy(v.法人税.加減算値s, '減算'));
    });
    const 減算小計 = this.create納税予測ListItem('③　小計', 減算小計CalcCells, ListItemConfig.subTotal);

    // 利益加減算小計
    const 利益加減算小計CalcCells = cデータ.yearValues.map((_, i) => {
      const fixes = this.getCellFixNumbers(税引前当期利益CalcCells[i], 加算小計CalcCells[i], 減算小計CalcCells[i]);
      return this.createFixCell(CellTagType.currency, fixes[0] + fixes[1] - fixes[2]);
    });
    const 利益加減算小計 = this.create納税予測ListItem(
      '④　小計（①＋②－③）',
      利益加減算小計CalcCells,
      ListItemConfig.subTotal
    );

    // 欠損金控除額
    const 欠損金控除額CalcCells = cデータ.yearValues.map((v) => {
      return this.createFixCell(CellTagType.currency, v.繰越欠損金.減少額);
    });
    const 欠損金控除額 = this.create納税予測ListItem('⑤　欠損金控除額', 欠損金控除額CalcCells, ListItemConfig.subTotal);

    // 課税所得
    const 課税所得CalcCells = cデータ.yearValues.map((_, i) => {
      const fixes = this.getCellFixNumbers(利益加減算小計CalcCells[i], 欠損金控除額CalcCells[i]);
      return this.createFixCell(CellTagType.currency, fixes[0] - fixes[1]);
    });
    const 課税所得 = this.create納税予測ListItem(
      '課税所得金額又は欠損金額（④－⑤）',
      課税所得CalcCells,
      ListItemConfig.subTotal
    );

    // 実行税率
    const 実行税率InputCells = cデータ.yearValues.map((v) =>
      this.createInputCell(CellTagType.inputPercent, v.法人税, '実効税率', '%')
    );
    const 実行税率 = this.create納税予測ListItem('実行税率', 実行税率InputCells, ListItemConfig.input);

    // 法人税
    const 法人税CalcCells = cデータ.yearValues.map((v, i) => {
      const fix = this.calcPercentByBigNumber(
        this.roundCellFixNumb(this.getCellFixNumbers(課税所得CalcCells[i])[0], 1000),
        v.法人税.実効税率,
        100,
        this.bnRoundingMode.実績計画
      );
      const reference = {
        object: v.法人税,
        key: '法人税等',
      };
      return this.createCell(CellTagType.inputTotalCurrency, fix <= 0 ? 0 : this.roundCellFixNumb(fix, 100), reference);
    });
    const 法人税 = this.create納税予測ListItem(E集計転記科目.法人税等, 法人税CalcCells, ListItemConfig.total);

    // 予定納税額
    const 予定納税額InputCells = cデータ.yearValues.map((v) =>
      this.createInputCell(CellTagType.inputCurrency, v.法人税, '予定納税額')
    );
    const 予定納税額 = this.create納税予測ListItem('予定納税額', 予定納税額InputCells, ListItemConfig.input);

    // 未払法人税
    const 未払法人税CalcCells = cデータ.yearValues.map((v, i) => {
      const fixes = this.getCellNumbers(法人税CalcCells[i]);
      return this.createFixCell(CellTagType.currency, fixes[0] - v.法人税.予定納税額);
    });
    const 未払法人税 = this.create納税予測ListItem(
      E集計転記科目.未払法人税等,
      未払法人税CalcCells,
      ListItemConfig.total
    );

    return {
      title: '法人税等の試算',
      items: [
        税引前当期利益,
        加算Header,
        ...加算s,
        加算小計,
        減算Header,
        ...減算s,
        減算小計,
        利益加減算小計,
        欠損金控除額,
        課税所得,
        実行税率,
        法人税,
        予定納税額,
        未払法人税,
      ],
    };
  }

  /**
   * cデータ から繰越欠損金の 納税予測Table を生成します
   *
   * @param cデータ
   * @returns 納税予測Table
   */
  private create繰越欠損金Table(cデータ: Cデータ納税予測): 納税予測Table {
    // 増加額
    const 増加額InputCells = cデータ.yearValues.map((v) =>
      this.createInputCell(CellTagType.inputCurrency, v.繰越欠損金, '増加額')
    );
    const 増加額 = this.create納税予測ListItem('繰越欠損金増加額', 増加額InputCells, ListItemConfig.input);

    // 減少額
    const 減少額InputCells = cデータ.yearValues.map((v) =>
      this.createInputCell(CellTagType.inputCurrency, v.繰越欠損金, '減少額')
    );
    const 減少額 = this.create納税予測ListItem('繰越欠損金減少額', 減少額InputCells, ListItemConfig.input);

    // 期首残高/期末残高
    const 期首残高Cells: Cell[] = [];
    const 期末残高Cells: Cell[] = [];
    cデータ.yearValues.forEach((v, i) => {
      let 期首残高Cell: Cell;
      let 期末残高Cell: Cell;
      if (i === 0) {
        const 期末残高Fix = v.繰越欠損金.残高! + v.繰越欠損金.増加額 - v.繰越欠損金.減少額;
        期首残高Cell = this.createInputCell(CellTagType.inputCurrency, v.繰越欠損金, '残高');
        期末残高Cell = this.createFixCell(CellTagType.currency, 期末残高Fix);
      } else {
        const 期首残高Fix = this.getCellFixNumbers(期末残高Cells[i - 1])[0];
        const 期末残高Fix = 期首残高Fix + v.繰越欠損金.増加額 - v.繰越欠損金.減少額;
        期首残高Cell = this.createFixCell(CellTagType.currency, 期首残高Fix);
        期末残高Cell = this.createFixCell(CellTagType.currency, 期末残高Fix);
      }

      期首残高Cells.push(期首残高Cell);
      期末残高Cells.push(期末残高Cell);
    });
    const 期首残高 = this.create納税予測ListItem('繰越欠損金期首残高', 期首残高Cells, ListItemConfig.input期首残高);
    const 期末残高 = this.create納税予測ListItem('繰越欠損金期末残高', 期末残高Cells, ListItemConfig.subTotal);

    return {
      title: '繰越欠損金の試算',
      items: [期首残高, 増加額, 減少額, 期末残高],
    };
  }

  /**
   * cデータ から消費税の 納税予測Table を生成します
   *
   * @param cデータ
   * @returns 納税予測Table
   */
  private create消費税Table(cデータ: Cデータ納税予測): 納税予測Table {
    const v計画PlbscfService = this.計画PlbscfService!;

    // 課税売上 / 課税売上返還
    const 課税売上Cells: Cell[] = [];
    const 課税売上返還Cells: Cell[] = [];
    this.決算期.years.forEach((y) => {
      // PLBSCFから値を取得
      const 課税売上value = v計画PlbscfService.getValueByCodeAnd決算期年月(
        E税区分.課税売上,
        y.year,
        undefined,
        y.year > this.getFirstYear()
      );
      const 課税売上返還value = v計画PlbscfService.getValueByCodeAnd決算期年月(
        E税区分.課税売上返還,
        y.year,
        undefined,
        y.year > this.getFirstYear()
      );
      // 消費税に換算し配列に追加
      課税売上Cells.push(this.createFixCell(CellTagType.currency, this.get消費税(課税売上value)));
      課税売上返還Cells.push(this.createFixCell(CellTagType.currency, this.get消費税(課税売上返還value)));
    });
    const 課税売上 = this.create納税予測ListItem('課税売上に係る消費税額', 課税売上Cells, ListItemConfig.subTotal);
    const 課税売上返還 = this.create納税予測ListItem(
      '課税売上に係る対価の返還額の消費税額',
      課税売上返還Cells,
      ListItemConfig.subTotal
    );

    // 返却用の納税予測ListItem配列生成
    const items: 納税予測ListItem[] = [課税売上, 課税売上返還];

    // 年税額の初期化
    const 年税額CalcCells: Cell[] = [];

    // 課税方式によって返却項目を変更する
    switch (AbstractデータService.事業所.c課税方式) {
      case E課税方式.本則課税:
        // 課税仕入 / 課税仕入返還
        const 課税仕入Cells: Cell[] = [];
        const 課税仕入返還Cells: Cell[] = [];
        // TODO: 上記のループとまとめられないか検討
        this.決算期.years.forEach((y, i) => {
          // PLBSCFから値を取得
          const plbscf課税仕入value = v計画PlbscfService.getValueByCodeAnd決算期年月(
            E税区分.課税仕入,
            y.year,
            undefined,
            y.year > this.getFirstYear()
          );
          const plbscf課税仕入返還value = v計画PlbscfService.getValueByCodeAnd決算期年月(
            E税区分.課税仕入返還,
            y.year,
            undefined,
            y.year > this.getFirstYear()
          );

          // 設備投資計画から値を取得
          const 設備投資課税仕入value = this.償却?.total.設備投資?.税区分別集計.課税仕入s[i] ?? 0;
          const 設備投資課税仕入返還value = this.償却?.total.設備投資?.税区分別集計.課税仕入返還s[i] ?? 0;

          // 消費税に換算し配列に追加
          課税仕入Cells.push(
            this.createFixCell(CellTagType.currency, this.get消費税(plbscf課税仕入value + 設備投資課税仕入value))
          );
          課税仕入返還Cells.push(
            this.createFixCell(
              CellTagType.currency,
              this.get消費税(plbscf課税仕入返還value + 設備投資課税仕入返還value)
            )
          );
        });
        const 課税仕入 = this.create納税予測ListItem('課税仕入に係る消費税額', 課税仕入Cells, ListItemConfig.subTotal);
        const 課税仕入返還 = this.create納税予測ListItem(
          '課税仕入に係る対価の返還額の消費税額',
          課税仕入返還Cells,
          ListItemConfig.subTotal
        );

        // 返却用の納税予測ListItem配列に追加
        items.push(課税仕入, 課税仕入返還);

        // 年税額の計算((課税売上 + 課税売上返還) - (課税仕入 + 課税仕入返還))
        cデータ.yearValues.forEach((_v, i) => {
          const profitFixes = this.getCellFixNumbers(課税売上Cells[i], 課税売上返還Cells[i]);
          const purchaseFixes = this.getCellFixNumbers(課税仕入Cells[i], 課税仕入返還Cells[i]);
          年税額CalcCells.push(
            this.createFixCell(
              CellTagType.currency,
              this.roundCellFixNumb(_.sum(profitFixes) - _.sum(purchaseFixes), 100)
            )
          );
        });

        break;

      case E課税方式.簡易課税:
        // みなし仕入れ率
        // みなし仕入れ率は消費税情報で設定された簡易課税事業区分により決定
        const みなし仕入れ率value = 100 - AbstractデータService.事業所.c簡易課税事業区分 * 10;

        const みなし仕入れ率Cells = new Array(cデータ.yearValues.length).fill(みなし仕入れ率value).map((v) => {
          return this.createFixCell(CellTagType.text, v, '%');
        });
        const みなし仕入れ率 = this.create納税予測ListItem(
          'みなし仕入れ率',
          みなし仕入れ率Cells,
          ListItemConfig.subTotal
        );

        // 返却用の納税予測ListItem配列に追加
        items.push(みなし仕入れ率);

        // 年税額の計算((課税売上 + 課税売上返還) × みなし仕入れ率)
        cデータ.yearValues.forEach((_v, i) => {
          const fixes = this.getCellFixNumbers(課税売上Cells[i], 課税売上返還Cells[i]);
          年税額CalcCells.push(
            this.createFixCell(
              CellTagType.currency,
              this.calcPercentByBigNumber(
                this.roundCellFixNumb(_.sum(fixes), 100),
                みなし仕入れ率value,
                100,
                this.bnRoundingMode.消費税
              )
            )
          );
        });

        break;
    }

    // 年税額
    const 年税額 = this.create納税予測ListItem('年税額', 年税額CalcCells, ListItemConfig.total);

    // 中間納付額
    const 中間納付額InputCells = cデータ.yearValues.map((v) =>
      this.createInputCell(CellTagType.inputCurrency, v, '消費税中間納付額')
    );
    const 予定納税額 = this.create納税予測ListItem('中間納付額', 中間納付額InputCells, ListItemConfig.input);

    // 未払消費税等
    const 未払消費税CalcCells = cデータ.yearValues.map((v, i) => {
      const fixes = this.getCellFixNumbers(年税額CalcCells[i]);
      return this.createFixCell(CellTagType.currency, fixes[0] - (v.消費税中間納付額 ?? 0));
    });
    const 未払消費税 = this.create納税予測ListItem(
      E集計転記科目.未払消費税等,
      未払消費税CalcCells,
      ListItemConfig.total
    );

    // 返却用の納税予測ListItem配列に追加
    items.push(年税額, 予定納税額, 未払消費税);

    return {
      title: `消費税（${E課税方式[AbstractデータService.事業所.c課税方式]}）の試算`,
      items,
    };
  }
}
